/**
 * Signature Template — v2 "Corporate Classic"
 *
 * Style: professional, structured, societe.com-like — sober white backgrounds,
 * strong typography hierarchy, clear section separators, tasteful entrance
 * animations & shine effects on buttons only.
 *
 * Fully customisable via `page`:
 *   - business_name, activity, description, address, phone, email
 *   - opening_hours, show_map, map_query
 *   - logo_url, banner_url (fallback only), gallery, services, testimonials
 *   - primary_color, secondary_color        (global fallbacks)
 *   - section_colors: { key: {bg, text, accent} } for each of 8 sections
 *   - button_colors:  { primary|secondary|phone: {bg, text} }
 *   - hero_slideshow: {enabled, autoplay, interval_ms, use_gallery, images}
 *   - sections: {hero,about,services,gallery,testimonials,hours,contact,map}
 *   - social_links, cta_text, cta_url, slug, siret
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Star,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Music2,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle2,
  Sparkles,
  Menu,
  Quote,
} from "lucide-react";

/* ---------- Constants ---------- */

const SOCIAL_META = {
  facebook:  { Icon: Facebook,  label: "Facebook"  },
  instagram: { Icon: Instagram, label: "Instagram" },
  linkedin:  { Icon: Linkedin,  label: "LinkedIn"  },
  twitter:   { Icon: Twitter,   label: "Twitter / X" },
  tiktok:    { Icon: Music2,    label: "TikTok"    },
  youtube:   { Icon: Youtube,   label: "YouTube"   },
};

const DAY_LABELS = {
  monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi",
  thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche",
  Lundi: "Lundi", Mardi: "Mardi", Mercredi: "Mercredi",
  Jeudi: "Jeudi", Vendredi: "Vendredi", Samedi: "Samedi", Dimanche: "Dimanche",
};

const DAY_ORDER = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

/* ---------- Utilities ---------- */

function todayFrLabel() {
  const idx = new Date().getDay();
  const map = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  return map[idx];
}

function normalizeSocial(social_links) {
  if (!social_links) return [];
  if (Array.isArray(social_links)) {
    return social_links
      .filter((l) => l && l.platform && l.url)
      .map((l) => ({ platform: l.platform, url: l.url }));
  }
  return Object.entries(social_links)
    .filter(([, url]) => typeof url === "string" && url.trim())
    .map(([platform, url]) => ({ platform, url }));
}

function hexToRgb(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return { r: 30, g: 42, b: 94 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Resolve section colours — locked to the main site palette
 * (customization removed to keep visual harmony across every business page).
 */
function resolveSection(key, page, primary, secondary) {
  const defaults = {
    hero:         { bg: "#FFFFFF",           text: "#0F172A",           accent: primary },
    about:        { bg: "#FFFFFF",           text: "#0F172A",           accent: primary },
    services:     { bg: "#F8FAFC",           text: "#0F172A",           accent: primary },
    gallery:      { bg: "#FFFFFF",           text: "#0F172A",           accent: primary },
    testimonials: { bg: "#F8FAFC",           text: "#0F172A",           accent: primary },
    hours:        { bg: "#FFFFFF",           text: "#0F172A",           accent: primary },
    contact:      { bg: "#F8FAFC",           text: "#0F172A",           accent: primary },
    footer:       { bg: "#0F172A",           text: "#FFFFFF",           accent: secondary },
  };
  return defaults[key] || { bg: "#FFFFFF", text: "#0F172A", accent: primary };
}

/** Resolve button style — locked to the main site palette. */
function resolveButton(key, page, primary, secondary) {
  const defaults = {
    primary:   { bg: primary,   text: "#FFFFFF" },
    secondary: { bg: "#FFFFFF", text: primary },
    phone:     { bg: secondary, text: "#FFFFFF" },
  };
  return defaults[key] || { bg: primary, text: "#FFFFFF" };
}

/* ---------- Animation presets ---------- */

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
  }),
};

/* ---------- Reusable pieces ---------- */

function SectionTitle({ kicker, title, accent, textColor }) {
  return (
    <div className="mb-10 md:mb-12">
      {kicker && (
        <motion.span
          variants={fadeUp}
          className="inline-block text-[11px] font-bold uppercase tracking-[0.25em] mb-3"
          style={{ color: accent }}
        >
          {kicker}
        </motion.span>
      )}
      {title && (
        <motion.h2
          variants={fadeUp}
          className="font-extrabold tracking-tight text-2xl sm:text-3xl md:text-4xl break-words"
          style={{ color: textColor }}
        >
          {title}
        </motion.h2>
      )}
      <motion.span
        variants={fadeUp}
        className="block h-[3px] w-14 mt-4 rounded-full"
        style={{ background: accent }}
      />
    </div>
  );
}

function Section({ id, colors, className = "", children }) {
  return (
    <section
      id={id}
      className={`relative w-full py-14 sm:py-18 lg:py-24 ${className}`}
      style={{ background: colors.bg, color: colors.text }}
    >
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.07 } } }}
        >
          {children}
        </motion.div>
      </div>
    </section>
  );
}

function BrandButton({ children, style, className = "", withShine = false, ...rest }) {
  return (
    <a
      {...rest}
      className={`sig-btn relative inline-flex items-center gap-2 rounded-full px-5 sm:px-6 py-3 text-sm font-bold shadow-sm transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 ${withShine ? "sig-btn-shine" : ""} ${className}`}
      style={{
        backgroundColor: style.bg,
        color: style.text,
        border: `1.5px solid ${style.bg === "#FFFFFF" || style.bg === "#ffffff" ? style.text : style.bg}`,
        ...style.extra,
      }}
    >
      {children}
    </a>
  );
}

/* ---------- StickyNav (classic) ---------- */

function StickyNav({ page, primary, secondary, sections, footerColors }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const phoneBtn = resolveButton("phone", page, primary, secondary);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [menuOpen]);

  const links = [
    { href: "#services",     label: "Services",     show: sections.services     && (page.services || []).length > 0 },
    { href: "#apropos",      label: "À propos",     show: sections.about        && !!page.description },
    { href: "#galerie",      label: "Galerie",      show: sections.gallery      && (page.gallery || []).length > 0 },
    { href: "#avis",         label: "Avis clients", show: sections.testimonials && (page.testimonials || []).length > 0 },
    { href: "#contact",      label: "Contact",      show: sections.contact },
  ].filter((l) => l.show);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-lg bg-white/95 border-b border-slate-200 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.15)]"
          : "bg-white/90 border-b border-slate-100"
      }`}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-5 py-3 lg:px-8">
        <a href="#top" className="flex items-center gap-3 group min-w-0">
          {page.logo_url ? (
            <img
              src={page.logo_url}
              alt={page.business_name}
              className="h-10 w-10 sm:h-11 sm:w-11 rounded-full object-cover ring-2 ring-slate-100 shadow-sm transition-transform group-hover:scale-105 shrink-0"
            />
          ) : (
            <div
              className="h-10 w-10 sm:h-11 sm:w-11 rounded-full flex items-center justify-center text-white font-black text-lg shadow-sm ring-2 ring-white/50 transition-transform group-hover:scale-105 shrink-0"
              style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
            >
              {(page.business_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-extrabold tracking-tight text-sm sm:text-base text-slate-900 truncate">
              {page.business_name}
            </span>
            {page.activity && (
              <span className="text-[10px] font-semibold uppercase tracking-wider truncate text-slate-500">
                {page.activity}
              </span>
            )}
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="px-3 py-2 rounded-md text-sm font-semibold text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {page.phone && (
            <a
              href={`tel:${page.phone}`}
              className="sig-btn-shine hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold shadow-md transition-transform hover:scale-[1.03]"
              style={{ background: phoneBtn.bg, color: phoneBtn.text }}
              data-testid="nav-phone-btn"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden lg:inline">{page.phone}</span>
              <span className="lg:hidden">Appeler</span>
            </a>
          )}
          {links.length > 0 && (
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Ouvrir le menu"
              data-testid="mobile-menu-toggle"
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-800 bg-white hover:bg-slate-50"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
          >
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 240, damping: 28 }}
              className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="mobile-menu-panel"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <span className="text-sm font-bold text-slate-900 truncate">{page.business_name}</span>
                <button onClick={() => setMenuOpen(false)} aria-label="Fermer" className="h-9 w-9 flex items-center justify-center rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto p-5 space-y-1">
                {links.map((l, i) => (
                  <motion.a
                    key={l.href} href={l.href} onClick={() => setMenuOpen(false)}
                    initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i, duration: 0.3 }}
                    className="block px-4 py-3 rounded-md text-base font-semibold text-slate-800 hover:bg-slate-100"
                  >
                    {l.label}
                  </motion.a>
                ))}
              </nav>
              <div className="p-5 border-t border-slate-200 space-y-2">
                {page.phone && (
                  <a
                    href={`tel:${page.phone}`}
                    className="flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-bold shadow-md"
                    style={{ background: phoneBtn.bg, color: phoneBtn.text }}
                  >
                    <Phone className="h-4 w-4" />
                    {page.phone}
                  </a>
                )}
                {page.email && (
                  <a
                    href={`mailto:${page.email}`}
                    className="flex items-center justify-center gap-2 rounded-full border-2 px-4 py-3 text-sm font-bold"
                    style={{ borderColor: primary, color: primary }}
                  >
                    <Mail className="h-4 w-4" />
                    Écrire un message
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

/* ---------- Hero (Slideshow, replaces banner) ---------- */

function HeroSlideshow({ page, primary, secondary, colors }) {
  const slideshow = page.hero_slideshow || {};
  // Choose images: dedicated hero slideshow images > gallery (if use_gallery)
  const heroImages = useMemo(() => {
    const dedicated = Array.isArray(slideshow.images) ? slideshow.images.filter(Boolean) : [];
    if (dedicated.length > 0) return dedicated;
    if (slideshow.use_gallery !== false && Array.isArray(page.gallery)) return page.gallery.filter(Boolean);
    return [];
  }, [slideshow.images, slideshow.use_gallery, page.gallery]);

  const [idx, setIdx] = useState(0);
  const autoplay = slideshow.autoplay !== false;
  const intervalMs = Math.max(1500, Math.min(20000, slideshow.interval_ms || 5000));

  useEffect(() => {
    if (!autoplay || heroImages.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % heroImages.length), intervalMs);
    return () => clearInterval(t);
  }, [autoplay, intervalMs, heroImages.length]);

  const primaryBtn = resolveButton("primary", page, primary, secondary);
  const secondaryBtn = resolveButton("secondary", page, primary, secondary);
  const phoneBtn = resolveButton("phone", page, primary, secondary);

  const hasImages = heroImages.length > 0;
  const goto = (n) => setIdx(((n % heroImages.length) + heroImages.length) % heroImages.length);

  return (
    <section id="top" className="relative overflow-hidden" style={{ background: colors.bg }} data-testid="hero-section">
      <div className="mx-auto max-w-6xl px-5 lg:px-8 pt-24 sm:pt-28 pb-14 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center">
          {/* Left: text */}
          <motion.div
            initial="hidden" animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.09 } } }}
          >
            <motion.div variants={fadeUp}>
              <span
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{ borderColor: rgba(colors.accent, 0.35), color: colors.accent, background: rgba(colors.accent, 0.06) }}
              >
                <Sparkles className="h-3 w-3" />
                {page.activity || "Bienvenue"}
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="mt-5 font-extrabold leading-[1.05] tracking-tight text-3xl sm:text-4xl lg:text-5xl break-words"
              style={{ color: colors.text }}
            >
              {page.business_name || "Votre entreprise"}
            </motion.h1>

            {page.description && (
              <motion.p
                variants={fadeUp}
                className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed break-words"
                style={{ color: rgba(colors.text, 0.75), overflowWrap: "anywhere" }}
              >
                {page.description}
              </motion.p>
            )}

            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
              {page.cta_text && page.cta_url && (
                <a
                  href={page.cta_url} target="_blank" rel="noopener noreferrer"
                  className="sig-btn-shine group inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold shadow-lg transition-transform hover:scale-[1.03]"
                  style={{ background: primaryBtn.bg, color: primaryBtn.text }}
                  data-testid="hero-cta-btn"
                >
                  {page.cta_text}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              )}
              {page.phone && (
                <a
                  href={`tel:${page.phone}`}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-3.5 text-sm font-bold shadow-sm transition-colors"
                  style={{ background: phoneBtn.bg, color: phoneBtn.text }}
                >
                  <Phone className="h-4 w-4" />
                  {page.phone}
                </a>
              )}
              {page.email && (
                <a
                  href={`mailto:${page.email}`}
                  className="inline-flex items-center gap-2 rounded-full border-2 px-5 py-3.5 text-sm font-bold transition-colors"
                  style={{ borderColor: secondaryBtn.text, background: secondaryBtn.bg, color: secondaryBtn.text }}
                >
                  <Mail className="h-4 w-4" />
                  Écrire
                </a>
              )}
            </motion.div>
          </motion.div>

          {/* Right: slideshow */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-[0_25px_60px_-24px_rgba(15,23,42,0.35)] ring-1 ring-slate-200"
            data-testid="hero-slideshow"
          >
            {hasImages ? (
              <>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={heroImages[idx]}
                    src={heroImages[idx]}
                    alt=""
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
                {/* Gradient overlay bottom */}
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

                {/* Controls */}
                {heroImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => goto(idx - 1)}
                      aria-label="Précédent"
                      data-testid="hero-slideshow-prev"
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center text-slate-900"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => goto(idx + 1)}
                      aria-label="Suivant"
                      data-testid="hero-slideshow-next"
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center text-slate-900"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                      {heroImages.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setIdx(i)}
                          aria-label={`Slide ${i + 1}`}
                          data-testid={`hero-slideshow-dot-${i}`}
                          className="h-1.5 rounded-full transition-all"
                          style={{
                            width: i === idx ? 22 : 8,
                            background: i === idx ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              /* Fallback when no images — colour block using accent + business initial */
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
              >
                <div className="text-center text-white/90">
                  <div className="text-6xl sm:text-7xl font-black">
                    {(page.business_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.3em] font-bold opacity-80">
                    Ajoutez des photos
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Compact header (when Hero disabled) ---------- */

function CompactHeader({ page, primary, secondary, colors }) {
  return (
    <section
      id="top"
      data-testid="compact-header"
      className="relative pt-24 pb-14 sm:pt-28 sm:pb-16 border-b"
      style={{ background: colors.bg, color: colors.text, borderColor: rgba(colors.text, 0.08) }}
    >
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        {page.activity && (
          <span
            className="inline-block text-[11px] font-bold uppercase tracking-[0.25em]"
            style={{ color: colors.accent }}
          >
            {page.activity}
          </span>
        )}
        <h1 className="mt-3 font-extrabold leading-tight tracking-tight text-3xl sm:text-4xl">
          {page.business_name}
        </h1>
      </div>
    </section>
  );
}

/* ---------- Services ---------- */

function ServicesSection({ page, primary, secondary, colors }) {
  const services = page.services || [];
  if (services.length === 0) return null;
  return (
    <Section id="services" colors={colors}>
      <SectionTitle kicker="Nos services" title="Ce que nous proposons" accent={colors.accent} textColor={colors.text} />
      <motion.div
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
        className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
      >
        {services.map((s, i) => (
          <motion.article
            key={i}
            variants={fadeUp}
            whileHover={{ y: -4 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="group relative overflow-hidden rounded-xl border bg-white p-6 shadow-[0_2px_10px_-2px_rgba(15,23,42,0.06)] hover:shadow-[0_14px_36px_-14px_rgba(15,23,42,0.20)] transition-shadow"
            style={{ borderColor: rgba(colors.text, 0.10) }}
          >
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg shadow-sm"
              style={{ background: colors.accent, color: "#fff" }}
            >
              <CheckCircle2 className="h-5 w-5" strokeWidth={2.25} />
            </div>
            <h3 className="text-base font-bold" style={{ color: colors.text }}>{s.name || `Service ${i + 1}`}</h3>
            {s.description && <p className="mt-1.5 text-sm leading-relaxed" style={{ color: rgba(colors.text, 0.7) }}>{s.description}</p>}
            {s.price && (
              <div
                className="mt-4 inline-block rounded-full px-3 py-1 text-xs font-bold"
                style={{ background: rgba(colors.accent, 0.1), color: colors.accent }}
              >
                {s.price}
              </div>
            )}
          </motion.article>
        ))}
      </motion.div>
    </Section>
  );
}

/* ---------- About ---------- */

function AboutSection({ page, primary, secondary, colors }) {
  const aboutText = page.about_text || page.description;
  if (!aboutText) return null;
  return (
    <Section id="apropos" colors={colors}>
      <SectionTitle kicker="À propos" title="Notre histoire" accent={colors.accent} textColor={colors.text} />
      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[1.5fr_1fr]">
        <motion.div variants={fadeUp} className="space-y-5 text-base leading-relaxed">
          <p style={{ color: rgba(colors.text, 0.85), overflowWrap: "anywhere" }}>{aboutText}</p>
          {page.address && (
            <div className="flex items-start gap-3" style={{ color: rgba(colors.text, 0.75) }}>
              <MapPin className="mt-0.5 h-5 w-5 shrink-0" style={{ color: colors.accent }} />
              <span style={{ overflowWrap: "anywhere" }}>{page.address}</span>
            </div>
          )}
        </motion.div>
        <motion.aside
          variants={fadeUp}
          className="relative overflow-hidden rounded-2xl p-7 text-white shadow-[0_18px_50px_-20px_rgba(15,23,42,0.5)]"
          style={{ background: `linear-gradient(135deg, ${colors.accent} 0%, ${secondary} 100%)` }}
        >
          <span className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
          <span className="absolute -bottom-16 -left-8 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/80">Contactez-nous</div>
            <div className="mt-5 space-y-3 text-sm">
              {page.phone && (
                <a href={`tel:${page.phone}`} className="flex items-center gap-3 hover:opacity-80">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15"><Phone className="h-4 w-4" /></span>
                  <span style={{ overflowWrap: "anywhere" }}>{page.phone}</span>
                </a>
              )}
              {page.email && (
                <a href={`mailto:${page.email}`} className="flex items-center gap-3 hover:opacity-80">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15"><Mail className="h-4 w-4" /></span>
                  <span className="break-all" style={{ overflowWrap: "anywhere" }}>{page.email}</span>
                </a>
              )}
              {page.address && (
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15"><MapPin className="h-4 w-4" /></span>
                  <span style={{ overflowWrap: "anywhere" }}>{page.address}</span>
                </div>
              )}
            </div>
          </div>
        </motion.aside>
      </div>
    </Section>
  );
}

/* ---------- Gallery ---------- */

function GallerySection({ page, primary, secondary, colors }) {
  const gallery = page.gallery || [];
  const [lightbox, setLightbox] = useState(null);
  const gLen = gallery.length;

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowLeft") setLightbox((i) => (i === null ? null : (i - 1 + gLen) % gLen));
      if (e.key === "ArrowRight") setLightbox((i) => (i === null ? null : (i + 1) % gLen));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, gLen]);

  if (gLen === 0) return null;

  return (
    <Section id="galerie" colors={colors}>
      <SectionTitle kicker="Galerie" title="Nos réalisations" accent={colors.accent} textColor={colors.text} />
      <motion.div
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
      >
        {gallery.map((src, i) => (
          <motion.button
            key={i}
            variants={fadeUp}
            onClick={() => setLightbox(i)}
            whileHover={{ scale: 1.02 }}
            className="group relative aspect-square overflow-hidden rounded-lg bg-slate-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ "--tw-ring-color": colors.accent }}
          >
            <img src={src} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            <span
              className="pointer-events-none absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
              style={{ background: `linear-gradient(135deg, ${rgba(colors.accent, 0.45)}, ${rgba(secondary, 0.3)})` }}
            />
          </motion.button>
        ))}
      </motion.div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          >
            <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 h-11 w-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"><X className="h-5 w-5" /></button>
            <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + gLen) % gLen); }} className="absolute left-4 sm:left-8 h-12 w-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronLeft className="h-6 w-6" /></button>
            <motion.img
              key={lightbox} src={gallery[lightbox]} alt=""
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % gLen); }} className="absolute right-4 sm:right-8 h-12 w-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronRight className="h-6 w-6" /></button>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold text-white">{lightbox + 1} / {gLen}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  );
}

/* ---------- Testimonials ---------- */

function TestimonialsSection({ page, primary, secondary, colors }) {
  const testimonials = page.testimonials || [];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (testimonials.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % testimonials.length), 6000);
    return () => clearInterval(t);
  }, [testimonials.length]);

  if (testimonials.length === 0) return null;
  const current = testimonials[idx] || testimonials[0];

  return (
    <Section id="avis" colors={colors}>
      <SectionTitle kicker="Avis clients" title="Ils nous ont fait confiance" accent={colors.accent} textColor={colors.text} />
      <div className="relative overflow-hidden rounded-2xl border bg-white p-7 sm:p-10 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)]" style={{ borderColor: rgba(colors.text, 0.10) }}>
        <Quote className="absolute top-5 left-5 h-9 w-9 opacity-15" style={{ color: colors.accent }} />
        <span className="absolute -top-16 -right-16 h-56 w-56 rounded-full blur-3xl" style={{ background: rgba(secondary, 0.18) }} />
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10"
          >
            <div className="flex gap-1 mb-4" style={{ color: colors.accent }}>
              {Array.from({ length: Math.max(1, Math.min(5, current.rating || 5)) }).map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-current" />
              ))}
            </div>
            <blockquote
              className="font-serif italic text-[15px] sm:text-base leading-relaxed"
              style={{ color: rgba(colors.text, 0.88), overflowWrap: "anywhere", fontWeight: 400 }}
            >
              « {current.text} »
            </blockquote>
            <footer className="mt-5 flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-full text-white font-black shadow-sm" style={{ background: `linear-gradient(135deg, ${colors.accent}, ${secondary})` }}>
                {(current.author || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-bold truncate" style={{ color: colors.text }}>{current.author || "Client anonyme"}</div>
                {current.role && <div className="text-sm truncate" style={{ color: rgba(colors.text, 0.6) }}>{current.role}</div>}
              </div>
            </footer>
          </motion.div>
        </AnimatePresence>
        {testimonials.length > 1 && (
          <div className="relative z-10 mt-7 flex items-center gap-1.5">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Avis ${i + 1}`}
                className="h-1.5 rounded-full transition-all"
                style={{ width: i === idx ? 24 : 8, background: i === idx ? colors.accent : rgba(colors.text, 0.2) }}
              />
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

/* ---------- Hours ---------- */

function OpeningHoursCard({ opening_hours, colors, textColor }) {
  const today = todayFrLabel();
  if (!opening_hours || opening_hours.length === 0) return null;
  const sorted = [...opening_hours].sort(
    (a, b) => DAY_ORDER.indexOf(DAY_LABELS[a.day] || a.day) - DAY_ORDER.indexOf(DAY_LABELS[b.day] || b.day)
  );
  return (
    <div className="rounded-xl border bg-white p-6 shadow-[0_2px_10px_-2px_rgba(15,23,42,0.06)]" style={{ borderColor: rgba(textColor, 0.10) }}>
      <div className="flex items-center gap-3 mb-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg text-white shadow-sm" style={{ background: colors.accent }}>
          <Clock className="h-5 w-5" />
        </span>
        <h3 className="text-base font-bold" style={{ color: textColor }}>Horaires d&apos;ouverture</h3>
      </div>
      <ul className="divide-y" style={{ borderColor: rgba(textColor, 0.08) }}>
        {sorted.map((h, i) => {
          const label = DAY_LABELS[h.day] || h.day;
          const isToday = label === today;
          return (
            <li
              key={i}
              className={`flex items-center justify-between py-2.5 text-sm ${isToday ? "font-bold" : ""}`}
              style={{ color: isToday ? colors.accent : rgba(textColor, 0.75), borderColor: rgba(textColor, 0.08) }}
            >
              <span className="flex items-center gap-2">
                {isToday && <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: colors.accent }} />}
                {label}
                {isToday && <span className="ml-2 text-[10px] uppercase tracking-widest">Aujourd&apos;hui</span>}
              </span>
              <span>{h.closed ? "Fermé" : `${h.open || "—"} – ${h.close || "—"}`}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ---------- Contact + Map ---------- */

function SocialBar({ social_links, colors, textColor }) {
  const items = normalizeSocial(social_links);
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((s, i) => {
        const meta = SOCIAL_META[s.platform] || { Icon: Sparkles, label: s.platform };
        const Icon = meta.Icon;
        return (
          <a
            key={i}
            href={s.url} target="_blank" rel="noopener noreferrer" aria-label={meta.label}
            className="flex h-10 w-10 items-center justify-center rounded-full border transition-all hover:-translate-y-0.5 hover:shadow-md"
            style={{ color: colors.accent, borderColor: rgba(textColor, 0.15), background: "#fff" }}
          >
            <Icon className="h-4 w-4" />
          </a>
        );
      })}
    </div>
  );
}

function ContactSection({ page, primary, secondary, colors, showHours, showMap, hoursColors }) {
  const hasHours = showHours && (page.opening_hours || []).length > 0;
  const wantMap = showMap && page.show_map && (page.map_query || page.address);
  const socialItems = normalizeSocial(page.social_links);

  return (
    <Section id="contact" colors={colors}>
      <SectionTitle kicker="Contact" title="Rendez-nous visite" accent={colors.accent} textColor={colors.text} />
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <motion.div variants={fadeUp} className="space-y-4">
          <div className="rounded-xl border bg-white p-6 shadow-[0_2px_10px_-2px_rgba(15,23,42,0.06)]" style={{ borderColor: rgba(colors.text, 0.10) }}>
            <h3 className="text-base font-bold mb-4" style={{ color: colors.text }}>Informations</h3>
            <ul className="space-y-3 text-sm">
              {page.phone && (
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white" style={{ background: colors.accent }}>
                    <Phone className="h-4 w-4" />
                  </span>
                  <a href={`tel:${page.phone}`} className="font-semibold hover:opacity-70" style={{ color: colors.text, overflowWrap: "anywhere" }}>{page.phone}</a>
                </li>
              )}
              {page.email && (
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white" style={{ background: colors.accent }}>
                    <Mail className="h-4 w-4" />
                  </span>
                  <a href={`mailto:${page.email}`} className="font-semibold hover:opacity-70 break-all" style={{ color: colors.text, overflowWrap: "anywhere" }}>{page.email}</a>
                </li>
              )}
              {page.address && (
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white" style={{ background: colors.accent }}>
                    <MapPin className="h-4 w-4" />
                  </span>
                  <span className="font-semibold" style={{ color: colors.text, overflowWrap: "anywhere" }}>{page.address}</span>
                </li>
              )}
            </ul>
            {socialItems.length > 0 && (
              <div className="mt-5 pt-5 border-t" style={{ borderColor: rgba(colors.text, 0.10) }}>
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-3" style={{ color: rgba(colors.text, 0.55) }}>Suivez-nous</div>
                <SocialBar social_links={page.social_links} colors={colors} textColor={colors.text} />
              </div>
            )}
          </div>
          {hasHours && <OpeningHoursCard opening_hours={page.opening_hours} colors={hoursColors} textColor={hoursColors.text} />}
        </motion.div>

        <motion.div variants={fadeUp} className="min-h-[400px]">
          {wantMap ? (
            <div className="h-full min-h-[400px] overflow-hidden rounded-xl border shadow-[0_2px_10px_-2px_rgba(15,23,42,0.06)]" style={{ borderColor: rgba(colors.text, 0.10) }}>
              <iframe
                title={`Carte — ${page.business_name}`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(page.map_query || page.address || page.business_name)}&output=embed`}
                className="h-full w-full border-0"
                loading="lazy" allowFullScreen
              />
            </div>
          ) : (
            <div
              className="relative flex h-full min-h-[400px] flex-col justify-between overflow-hidden rounded-xl p-7 text-white shadow-[0_18px_50px_-20px_rgba(15,23,42,0.5)]"
              style={{ background: `linear-gradient(135deg, ${colors.accent}, ${secondary})` }}
            >
              <span className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
              <span className="absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
              <div className="relative">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/80">Une question ?</div>
                <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold leading-tight">Nous sommes à votre écoute.</h3>
                <p className="mt-4 text-white/90 leading-relaxed">
                  Contactez-nous par téléphone, par email ou passez nous voir. Nous vous répondons rapidement.
                </p>
              </div>
              {(page.phone || page.email) && (
                <div className="relative flex flex-wrap gap-3 mt-6">
                  {page.phone && (
                    <a href={`tel:${page.phone}`} className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-5 py-2.5 text-sm font-bold hover:bg-white/25">
                      <Phone className="h-4 w-4" /> Appeler
                    </a>
                  )}
                  {page.email && (
                    <a href={`mailto:${page.email}`} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold hover:bg-white/90" style={{ color: colors.accent }}>
                      <Mail className="h-4 w-4" /> Écrire
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </Section>
  );
}

/* ---------- Footer ---------- */

function Footer({ page, colors }) {
  return (
    <footer className="border-t py-8" style={{ background: colors.bg, color: colors.text, borderColor: rgba(colors.text, 0.12) }}>
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 text-center text-xs sm:flex-row sm:text-left lg:px-8">
        <div style={{ color: rgba(colors.text, 0.7) }}>
          © {new Date().getFullYear()}{" "}
          <span className="font-bold" style={{ color: colors.text }}>{page.business_name}</span>
          {page.siret && <span className="ml-2" style={{ color: rgba(colors.text, 0.5) }}>· SIRET {page.siret}</span>}
        </div>
        <div style={{ color: rgba(colors.text, 0.7) }}>
          Propulsé par{" "}
          <a
            href="https://leguidedesentrepreneurs.fr" target="_blank" rel="noopener noreferrer"
            className="font-bold hover:underline" style={{ color: colors.accent }}
          >
            Le Guide des Entrepreneurs
          </a>
        </div>
      </div>
    </footer>
  );
}

/* ---------- Global styles ---------- */

const GLOBAL_STYLES = `
  @keyframes sig-shine {
    0%   { transform: translateX(-120%) skewX(-20deg); }
    60%  { transform: translateX(120%)  skewX(-20deg); }
    100% { transform: translateX(120%)  skewX(-20deg); }
  }
  .sig-btn-shine { position: relative; overflow: hidden; }
  .sig-btn-shine::before {
    content: "";
    position: absolute; inset: 0;
    background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%);
    transform: translateX(-120%) skewX(-20deg);
    animation: sig-shine 3.6s ease-in-out infinite;
    pointer-events: none;
  }
`;

/* ---------- Main renderer ---------- */

const SignatureTemplate = ({ page }) => {
  // Locked to the main site's brand palette — no per-user customization.
  const primary = "#1E2A5E";
  const secondary = "#2FA398";

  const sections = useMemo(() => ({
    hero: true, about: true, services: true, gallery: true,
    testimonials: true, hours: true, contact: true, map: true,
    ...(page.sections || {}),
  }), [page.sections]);

  const enrichedPage = useMemo(() => ({
    ...page,
    business_name: page.business_name || "Votre entreprise",
  }), [page]);

  // Resolve colours once per render
  const heroC     = resolveSection("hero",         enrichedPage, primary, secondary);
  const aboutC    = resolveSection("about",        enrichedPage, primary, secondary);
  const servicesC = resolveSection("services",     enrichedPage, primary, secondary);
  const galleryC  = resolveSection("gallery",      enrichedPage, primary, secondary);
  const testC     = resolveSection("testimonials", enrichedPage, primary, secondary);
  const hoursC    = resolveSection("hours",        enrichedPage, primary, secondary);
  const contactC  = resolveSection("contact",      enrichedPage, primary, secondary);
  const footerC   = resolveSection("footer",       enrichedPage, primary, secondary);

  return (
    <div className="min-h-screen bg-white antialiased">
      <style>{GLOBAL_STYLES}</style>
      <StickyNav page={enrichedPage} primary={primary} secondary={secondary} sections={sections} footerColors={footerC} />
      {sections.hero
        ? <HeroSlideshow page={enrichedPage} primary={primary} secondary={secondary} colors={heroC} />
        : <CompactHeader page={enrichedPage} primary={primary} secondary={secondary} colors={heroC} />
      }
      {sections.services     && <ServicesSection     page={enrichedPage} primary={primary} secondary={secondary} colors={servicesC} />}
      {sections.about        && <AboutSection        page={enrichedPage} primary={primary} secondary={secondary} colors={aboutC} />}
      {sections.gallery      && <GallerySection      page={enrichedPage} primary={primary} secondary={secondary} colors={galleryC} />}
      {sections.testimonials && <TestimonialsSection page={enrichedPage} primary={primary} secondary={secondary} colors={testC} />}
      {sections.contact      && <ContactSection      page={enrichedPage} primary={primary} secondary={secondary} colors={contactC} showHours={sections.hours} showMap={sections.map} hoursColors={hoursC} />}
      <Footer page={enrichedPage} colors={footerC} />
    </div>
  );
};

const TemplateRenderer = ({ page }) => {
  if (!page) return null;
  return <SignatureTemplate page={page} />;
};

export default TemplateRenderer;
